/**
 * FFmpeg Service - Handles FFmpeg.wasm initialization and operations
 * Uses a singleton pattern to ensure only one FFmpeg instance is created
 */

// FFmpeg.wasm version that works with Create React App
const FFmpegBaseURL = 'https://unpkg.com/@ffmpeg/core@0.10.0/dist/';

let ffmpegInstance = null;
let isLoaded = false;
let progressCallback = null;
let statusCallback = null;
let isProcessing = false;
let shouldCancel = false;

/**
 * Initialize FFmpeg instance
 * @returns {Promise<void>}
 */
export const initFFmpeg = async () => {
  try {
    if (!window.FFmpeg) {
      throw new Error('FFmpeg not loaded. Please check your internet connection and try again.');
    }

    if (!ffmpegInstance) {
      const { createFFmpeg } = window.FFmpeg;
      
      ffmpegInstance = createFFmpeg({
        log: true,
        corePath: `${FFmpegBaseURL}ffmpeg-core.js`,
        progress: (progress) => {
          if (progressCallback && progress && progress.ratio >= 0) {
            progressCallback(Math.round(progress.ratio * 100));
          }
        }
      });
    }

    if (!isLoaded) {
      await ffmpegInstance.load();
      isLoaded = true;
    }
    
    return true;
  } catch (error) {
    console.error('FFmpeg initialization error:', error);
    throw error;
  }
};

/**
 * Check if FFmpeg is loaded
 * @returns {boolean}
 */
export const isFFmpegLoaded = () => {
  return isLoaded && ffmpegInstance !== null;
};

/**
 * Set progress callback function
 * @param {Function} callback - Progress callback function
 */
export const setProgressCallback = (callback) => {
  progressCallback = callback;
};

/**
 * Set status callback function for detailed status messages
 * @param {Function} callback - Status callback function
 */
export const setStatusCallback = (callback) => {
  statusCallback = callback;
};

/**
 * Update status message
 * @param {string} message - Status message
 */
const updateStatus = (message) => {
  if (statusCallback) {
    statusCallback(message);
  }
};

/**
 * Cancel current conversion
 */
export const cancelConversion = () => {
  shouldCancel = true;
};

/**
 * Force reinitialize FFmpeg instance (use when stuck)
 * @returns {Promise<void>}
 */
export const reinitializeFFmpeg = async () => {
  try {
    // Reset all state
    shouldCancel = false;
    isProcessing = false;
    
    // Terminate existing instance if it exists
    if (ffmpegInstance) {
      try {
        // Try to clean up any remaining files
        const files = ffmpegInstance.FS('readdir', '/');
        files.forEach(file => {
          if (file !== '.' && file !== '..' && file !== 'tmp' && file !== 'home' && file !== 'dev') {
            try {
              ffmpegInstance.FS('unlink', file);
            } catch (e) {
              // Ignore cleanup errors
            }
          }
        });
      } catch (e) {
        // Ignore cleanup errors
      }
    }
    
    // Force create new instance
    ffmpegInstance = null;
    isLoaded = false;
    
    // Reinitialize
    await initFFmpeg();
    
    console.log('FFmpeg instance reinitialized successfully');
  } catch (error) {
    console.error('Failed to reinitialize FFmpeg:', error);
    throw error;
  }
};

/**
 * Convert video file
 * @param {File} file - Video file to convert
 * @param {Object} options - Conversion options
 * @returns {Promise<Object>} - Converted video data
 */
export const convertVideo = async (file, options) => {
  if (!isFFmpegLoaded()) {
    throw new Error('FFmpeg not loaded. Please initialize FFmpeg first.');
  }

  // Check if already processing
  if (isProcessing) {
    throw new Error('FFmpeg is already processing. Please wait or cancel the current operation.');
  }

  // Reset cancellation flag and set processing state
  shouldCancel = false;
  isProcessing = true;

  try {
    const { fetchFile } = window.FFmpeg;
    
    // Check file size before processing
    const maxFileSize = 1024 * 1024 * 1024; // 1GB
    if (file.size > maxFileSize) {
      throw new Error(`File is too large (${Math.round(file.size / 1024 / 1024)}MB). Maximum supported file size is 1GB due to browser memory limitations. Try free desktop software like HandBrake, FFmpeg desktop, or VLC Media Player.`);
    }
    
    updateStatus('Reading video file...');
    
    // Write input file to FFmpeg virtual file system
    try {
      const fileData = await fetchFile(file);
      updateStatus('Loading video into processor...');
      ffmpegInstance.FS('writeFile', 'input.video', fileData);
    } catch (fetchError) {
      if (fetchError.message && fetchError.message.includes('File could not be read')) {
        throw new Error('Failed to read file. This may be due to insufficient memory for large files. Try with a smaller file or use desktop conversion software.');
      }
      throw new Error(`Failed to load file: ${fetchError.message || 'Unknown error'}`);
    }

    // Build conversion parameters
    let params = ['-i', 'input.video'];
    
    // Determine output format and extension
    let extension, mimeType;
    
    // Output format specific settings
    switch (options.outputFormat) {
      case 'hevc':
        params.push('-c:v', 'libx265');
        if (options.removeAudio) {
          params.push('-an');
        } else {
          params.push('-c:a', 'aac');
        }
        // HEVC quality settings
        const hevcQualityMap = {
          'low': ['-crf', '28'],
          'medium': ['-crf', '23'],
          'high': ['-crf', '18']
        };
        params.push(...hevcQualityMap[options.quality || 'medium']);
        // Add Apple compatibility tag for HEVC
        params.push('-tag:v', 'hvc1');
        extension = 'mp4';
        mimeType = 'video/mp4';
        break;
        
      case 'webm':
        params.push('-c:v', 'libvpx-vp9');
        if (options.removeAudio) {
          params.push('-an');
        } else {
          params.push('-c:a', 'libopus');
        }
        // WebM quality settings
        const webmQualityMap = {
          'low': ['-crf', '35'],
          'medium': ['-crf', '31'],
          'high': ['-crf', '24']
        };
        params.push(...webmQualityMap[options.quality || 'medium']);
        extension = 'webm';
        mimeType = 'video/webm';
        break;
        
      case 'mp3':
        params.push('-c:a', 'libmp3lame', '-vn'); // -vn removes video stream
        // MP3 quality settings
        const mp3QualityMap = {
          'low': ['-b:a', '128k'],
          'medium': ['-b:a', '192k'],
          'high': ['-b:a', '320k']
        };
        params.push(...mp3QualityMap[options.quality || 'medium']);
        extension = 'mp3';
        mimeType = 'audio/mpeg';
        break;
        
      case 'avi':
        params.push('-c:v', 'libx264');
        if (options.removeAudio) {
          params.push('-an');
        } else {
          params.push('-c:a', 'mp3');
        }
        // AVI quality settings
        const aviQualityMap = {
          'low': ['-crf', '28'],
          'medium': ['-crf', '23'],
          'high': ['-crf', '18']
        };
        params.push(...aviQualityMap[options.quality || 'medium']);
        extension = 'avi';
        mimeType = 'video/x-msvideo';
        break;
        
      case 'h264_mp4':
      default: // mp4 (H.264)
        params.push('-c:v', 'libx264');
        if (options.removeAudio) {
          params.push('-an');
        } else {
          params.push('-c:a', 'aac');
        }
        // H.264 quality settings
        const h264QualityMap = {
          'low': ['-crf', '28'],
          'medium': ['-crf', '23'],
          'high': ['-crf', '18']
        };
        params.push(...h264QualityMap[options.quality || 'medium']);
        extension = 'mp4';
        mimeType = 'video/mp4';
        break;
    }

    // Resolution settings (skip for audio-only MP3)
    if (options.resolution && options.resolution !== 'original' && options.outputFormat !== 'mp3') {
      params.push('-vf', `scale=${options.resolution}`);
    }

    params.push(`output.${extension}`);

    // Check for cancellation before starting conversion
    if (shouldCancel) {
      throw new Error('Conversion cancelled by user');
    }

    updateStatus('Converting video... This may take a while.');
    
    // Run conversion
    await ffmpegInstance.run(...params);
    
    updateStatus('Finalizing conversion...');
    
    // Check for cancellation after conversion
    if (shouldCancel) {
      throw new Error('Conversion cancelled by user');
    }

    updateStatus('Reading converted file...');
    
    // Read output file
    let outputData;
    try {
      outputData = ffmpegInstance.FS('readFile', `output.${extension}`);
    } catch (readError) {
      throw new Error('Failed to read converted file. The conversion may have failed silently.');
    }
    
    // Clean up virtual file system
    updateStatus('Cleaning up...');
    try {
      ffmpegInstance.FS('unlink', 'input.video');
    } catch (e) { /* ignore cleanup errors */ }
    try {
      ffmpegInstance.FS('unlink', `output.${extension}`);
    } catch (e) { /* ignore cleanup errors */ }

    updateStatus('Conversion complete!');
    
    return {
      data: outputData.buffer,
      extension,
      mimeType
    };
  } catch (error) {
    console.error('Video conversion error:', error);
    
    // If we get a "can only run one command at a time" error, reinitialize
    if (error.message && error.message.includes('can only run one command at a time')) {
      console.log('FFmpeg stuck, attempting to reinitialize...');
      try {
        await reinitializeFFmpeg();
      } catch (reinitError) {
        console.error('Failed to reinitialize FFmpeg:', reinitError);
      }
    }
    
    throw error;
  } finally {
    // Always reset processing state
    isProcessing = false;
    shouldCancel = false;
  }
};
